// VRT cart drawer controller. One <vrt-cart> exists globally. It opens on any
// [data-vrt-cart-toggle] click, adds products from any [data-vrt-quick-add]
// button, and changes/removes lines from within the drawer. Every mutation goes
// through the Cart AJAX API with `sections: ['vrt-cart-drawer']`, and the returned
// pre-rendered HTML replaces [data-vrt-cart-content] — so all money/locale
// formatting stays in Liquid.

const SECTION_ID = 'vrt-cart-drawer';

async function cartPost(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

class VrtCart extends HTMLElement {
  connectedCallback() {
    // Close / change actions inside the drawer (delegated; survives innerHTML swaps).
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-vrt-cart-close]')) {
        this.close();
        return;
      }
      const change = e.target.closest('[data-vrt-cart-change]');
      if (change) {
        this.change(change.dataset.lineKey, change.dataset.qty);
      }
    });

    // Global: open buttons + quick-add buttons.
    this._onDocClick = (e) => {
      const toggle = e.target.closest('[data-vrt-cart-toggle]');
      if (toggle) {
        e.preventDefault();
        this.open();
        return;
      }
      const add = e.target.closest('[data-vrt-quick-add]');
      if (add) {
        e.preventDefault();
        e.stopPropagation();
        this.quickAdd(add);
      }
    };
    document.addEventListener('click', this._onDocClick);

    this._onKey = (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    };
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }

  get panel() {
    return this.querySelector('.vrt-cart__panel');
  }

  open() {
    this.classList.add('is-open');
    document.body.classList.add('vrt-no-scroll');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'false');
    const close = this.querySelector('[data-vrt-cart-close]');
    if (close && close.focus) close.focus();
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('vrt-no-scroll');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
  }

  applySections(sections) {
    const html = sections && sections[SECTION_ID];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-vrt-cart-content]');
    const live = this.querySelector('[data-vrt-cart-content]');
    if (fresh && live) live.innerHTML = fresh.innerHTML;

    // Sync every cart-count badge on the page from the freshly rendered drawer.
    const live2 = this.querySelector('[data-vrt-cart-count]');
    if (live2) {
      const val = live2.textContent.trim();
      document.querySelectorAll('[data-vrt-cart-count]').forEach((el) => {
        el.textContent = val;
      });
    }
  }

  async change(lineKey, qty) {
    if (!lineKey) return;
    try {
      const data = await cartPost('/cart/change.js', {
        id: lineKey,
        quantity: Number(qty),
        sections: [SECTION_ID],
      });
      this.applySections(data.sections);
    } catch (e) {
      /* network error — leave drawer as-is */
    }
  }

  async quickAdd(btn) {
    const id = btn.dataset.variantId;
    if (!id) return;
    const quantity = Number(btn.dataset.quantity) || 1;
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const data = await cartPost('/cart/add.js', {
        items: [{ id: Number(id), quantity }],
        sections: [SECTION_ID],
      });
      if (data.status) {
        btn.textContent = data.description || 'Unavailable';
      } else {
        this.applySections(data.sections);
        this.open();
        btn.textContent = 'Added ✓';
      }
    } catch (e) {
      btn.textContent = 'Try again';
    }
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = original;
    }, 1400);
  }
}

if (!customElements.get('vrt-cart')) {
  customElements.define('vrt-cart', VrtCart);
}
